import { realpathSync, statSync } from "node:fs";
import { isAbsolute, join, relative } from "node:path";
import type { AgentPromptInput } from "../types.js";
import type { NativeRequests } from "./native-protocol.generated.js";

const invalid = () => new Error("CODEX_INPUT_INVALID");
const inside = (root: string, path: string) => {
  const rel = relative(root, path);
  return rel !== "" && !rel.startsWith("..") && !isAbsolute(rel);
};

/** Resolve attachments against trusted workspace/skill roots, never private credential state. */
export function codexPromptInput(
  input: AgentPromptInput,
  cwd: string,
  skills?: { source: string; target: string },
): NativeRequests["turn/start"]["params"]["input"] {
  if (
    !input ||
    typeof input.text !== "string" ||
    input.text.length > 1024 * 1024 ||
    (input.attachments !== undefined && !Array.isArray(input.attachments)) ||
    (input.attachments?.length ?? 0) > 32
  )
    throw invalid();
  const result: NativeRequests["turn/start"]["params"]["input"] = [
    { type: "text", text: input.text },
  ];
  for (const attachment of input.attachments ?? []) {
    try {
      if (!attachment || typeof attachment.path !== "string") throw invalid();
      const root =
        attachment.type === "skill" && skills ? realpathSync(skills.source) : realpathSync(cwd);
      const path = realpathSync(
        isAbsolute(attachment.path) ? attachment.path : join(root, attachment.path),
      );
      if (!inside(root, path) || !statSync(path).isFile()) throw invalid();
      if (attachment.type === "image") result.push({ type: "localImage", path });
      else if (attachment.type === "file" || attachment.type === "skill") {
        if (!attachment.name || [...attachment.name].some((char) => char.charCodeAt(0) < 32))
          throw invalid();
        if (attachment.type === "skill") {
          if (!skills) throw invalid();
          const copied = realpathSync(join(skills.target, relative(root, path)));
          if (!inside(realpathSync(skills.target), copied) || !statSync(copied).isFile())
            throw invalid();
          result.push({ type: "skill", name: attachment.name, path: copied });
        } else result.push({ type: "mention", name: attachment.name, path });
      } else throw invalid();
    } catch {
      throw invalid();
    }
  }
  return result;
}
